import React, { useState } from 'react';
import { ChevronDown, ChevronRight, CheckCircle2, XCircle, Wrench, Wand2, User, Loader2, Layers } from 'lucide-react';
import { cn } from '@/utils/cn';
import type { ChatMessage as ChatMessageType } from '@/services/cowork.service';

interface ChatMessageProps {
  message: ChatMessageType;
  isLast?: boolean;
  onQuickReply?: (text: string) => void;
}

/** Map active tools to human-friendly stage labels */
function getStageLabel(activeTools: string[]): string {
  const current = activeTools[activeTools.length - 1];
  if (current === 'search_tools') return 'Finding relevant tools...';
  if (current === 'execute_code') return 'Executing operations...';
  if (current.startsWith('list_') || current.startsWith('get_')) return 'Fetching data...';
  if (current.startsWith('create_')) return 'Creating resource...';
  if (current.startsWith('update_') || current.startsWith('set_')) return 'Updating configuration...';
  if (current.startsWith('delete_') || current.startsWith('remove_')) return 'Processing deletion...';
  if (current.includes('dashboard') || current.includes('analytics') || current.includes('usage')) return 'Analyzing data...';
  if (current.includes('search') || current.includes('find')) return 'Searching...';
  return 'Processing...';
}

/** Simple markdown rendering */
function renderContent(content: string): React.ReactNode[] {
  const lines = content.split('\n');
  const elements: React.ReactNode[] = [];
  let inCodeBlock = false;
  let codeLines: string[] = [];
  let codeKey = 0;

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];

    if (line.startsWith('```')) {
      if (inCodeBlock) {
        elements.push(
          <pre key={`code-${codeKey++}`} className="my-2 p-3 bg-gray-900 text-gray-100 text-xs rounded-lg overflow-x-auto font-mono">
            <code>{codeLines.join('\n')}</code>
          </pre>
        );
        codeLines = [];
        inCodeBlock = false;
      } else {
        inCodeBlock = true;
      }
      continue;
    }

    if (inCodeBlock) { codeLines.push(line); continue; }
    if (line.trim() === '') { elements.push(<div key={`br-${i}`} className="h-2" />); continue; }

    if (line.startsWith('### ')) {
      elements.push(<h4 key={`h-${i}`} className="text-sm font-bold text-gray-900 mt-2 mb-1 font-heading">{renderInline(line.slice(4))}</h4>);
      continue;
    }
    if (line.startsWith('## ')) {
      elements.push(<h3 key={`h-${i}`} className="text-sm font-bold text-gray-900 mt-3 mb-1 font-heading">{renderInline(line.slice(3))}</h3>);
      continue;
    }
    if (line.startsWith('# ')) {
      elements.push(<h2 key={`h-${i}`} className="text-base font-bold text-gray-900 mt-3 mb-1 font-heading">{renderInline(line.slice(2))}</h2>);
      continue;
    }
    if (line.match(/^[\-\*]\s/)) {
      elements.push(<li key={`li-${i}`} className="ml-4 leading-relaxed list-disc">{renderInline(line.slice(2))}</li>);
      continue;
    }
    if (line.match(/^\d+\.\s/)) {
      const numEnd = line.indexOf('. ');
      elements.push(<li key={`li-${i}`} className="ml-4 leading-relaxed list-decimal">{renderInline(line.slice(numEnd + 2))}</li>);
      continue;
    }

    elements.push(<p key={`p-${i}`} className="leading-relaxed">{renderInline(line)}</p>);
  }

  if (inCodeBlock && codeLines.length > 0) {
    elements.push(
      <pre key={`code-${codeKey}`} className="my-2 p-3 bg-gray-900 text-gray-100 text-xs rounded-lg overflow-x-auto font-mono">
        <code>{codeLines.join('\n')}</code>
      </pre>
    );
  }

  return elements;
}

function renderInline(text: string): React.ReactNode[] {
  const parts: React.ReactNode[] = [];
  const regex = /(\*\*(.+?)\*\*|`([^`]+)`)/g;
  let lastIndex = 0;
  let match;
  let key = 0;

  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) parts.push(<span key={`t-${key++}`}>{text.slice(lastIndex, match.index)}</span>);
    if (match[2]) parts.push(<strong key={`b-${key++}`} className="font-semibold">{match[2]}</strong>);
    else if (match[3]) parts.push(<code key={`c-${key++}`} className="px-1 py-0.5 bg-gray-100 text-violet-700 text-xs rounded font-mono">{match[3]}</code>);
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < text.length) parts.push(<span key={`t-${key++}`}>{text.slice(lastIndex)}</span>);
  return parts;
}

/** Detect if assistant is asking for explicit confirmation before an action */
function detectQuickReplies(content: string, isLast: boolean): string[] | null {
  if (!isLast || !content) return null;

  // Only check the last 2 lines — confirmation always comes at the end
  const lines = content.trim().split('\n');
  const lastLines = lines.slice(-2).join(' ').toLowerCase();

  // Explicit create/modify confirmation
  if (lastLines.includes('want me to create it') || lastLines.includes('shall i create it') || lastLines.includes('ready to create')) {
    return ['Yes, create it', 'Make changes first'];
  }
  // Delete confirmation
  if (lastLines.includes('want me to delete') || lastLines.includes('shall i delete') || lastLines.includes('confirm delet')) {
    return ['Yes, delete it', 'No, keep it'];
  }
  // Config review confirmation
  if (lastLines.includes('does this look good') || lastLines.includes('look good?') || lastLines.includes('want me to proceed') || lastLines.includes('shall i proceed')) {
    return ['Looks good, proceed', 'Make changes'];
  }
  // Activate confirmation
  if (lastLines.includes('want me to activate') || lastLines.includes('shall i activate')) {
    return ['Yes, activate', 'Not yet'];
  }

  return null;
}

export const ChatMessageComponent: React.FC<ChatMessageProps> = ({ message, isLast, onQuickReply }) => {
  const [toolsExpanded, setToolsExpanded] = useState(false);
  const [compactExpanded, setCompactExpanded] = useState(false);
  const isUser = message.role === 'user';
  const quickReplies = !isUser && message.role !== 'system' ? detectQuickReplies(message.content, !!isLast && !message.isStreaming) : null;

  // Mode switch marker
  if (message.type === 'mode_switch') {
    const isAdvanced = message.content.includes('Advanced');
    return (
      <div className="flex justify-center mb-3">
        <div className={cn(
          'inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-[10px] font-medium',
          isAdvanced ? 'bg-violet-100 text-violet-700 border border-violet-200' : 'bg-blue-50 text-blue-600 border border-blue-200'
        )}>
          <span className={cn('w-1.5 h-1.5 rounded-full', isAdvanced ? 'bg-violet-500' : 'bg-blue-500')} />
          {message.content}
        </div>
      </div>
    );
  }

  // Compact marker — collapsible summary
  if (message.type === 'compact') {
    return (
      <div className="flex justify-center mb-4">
        <div className="max-w-md w-full">
          <button
            onClick={() => setCompactExpanded(!compactExpanded)}
            className={cn(
              'w-full flex items-center gap-2 px-3 py-2 rounded-lg text-xs transition-colors',
              'bg-amber-50 border border-amber-200 text-amber-700 hover:bg-amber-100'
            )}
          >
            <Layers className="w-3.5 h-3.5 shrink-0" />
            <span className="font-medium">Conversation compacted — {message.compacted_count || '?'} messages summarized</span>
            {message.usage?.cost_usd != null && (
              <span className="ml-auto text-[10px] text-amber-500">${(message.usage.cost_usd ?? 0) < 0.01 ? (message.usage.cost_usd ?? 0).toFixed(4) : (message.usage.cost_usd ?? 0).toFixed(2)}</span>
            )}
            {compactExpanded ? <ChevronDown className="w-3 h-3 shrink-0" /> : <ChevronRight className="w-3 h-3 shrink-0" />}
          </button>
          {compactExpanded && (
            <div className="mt-1 px-3 py-2 bg-amber-50/50 border border-amber-100 rounded-lg text-xs text-amber-800 whitespace-pre-wrap font-mono leading-relaxed max-h-48 overflow-y-auto">
              {message.content}
            </div>
          )}
        </div>
      </div>
    );
  }

  return (
    <div className={cn('flex gap-3 mb-4', isUser ? 'flex-row-reverse' : '')}>
      {/* Avatar */}
      <div className={cn(
        'w-7 h-7 rounded-lg border-2 border-[#1a1a2e] flex items-center justify-center shrink-0 mt-0.5',
        isUser ? 'bg-gray-100' : 'bg-violet-100'
      )}>
        {isUser ? <User className="w-3.5 h-3.5 text-gray-600" /> : <Wand2 className="w-3.5 h-3.5 text-violet-600" />}
      </div>

      {/* Content */}
      <div className={cn('flex-1 min-w-0', isUser ? 'text-right' : '')}>
        {isUser ? (
          <div className="inline-block text-sm text-gray-900 bg-gray-50 rounded-xl px-4 py-2 border border-gray-200">
            {message.content}
          </div>
        ) : (
          <>
            {/* Stage indicator — shows what the LLM is doing */}
            {message.activeTools && message.activeTools.length > 0 && (
              <div className="flex items-center gap-2 mb-2 text-xs text-violet-600">
                <Loader2 className="w-3 h-3 animate-spin" />
                <span>{getStageLabel(message.activeTools)}</span>
              </div>
            )}

            {/* Streaming / final content */}
            <div className="text-sm text-gray-800">
              {renderContent(message.content || '')}
              {/* Blinking cursor while streaming */}
              {message.isStreaming && (
                <span className="inline-block w-2 h-4 bg-violet-500 ml-0.5 animate-pulse rounded-sm" />
              )}
            </div>

            {/* Tool calls — collapsible */}
            {message.tool_calls && message.tool_calls.length > 0 && (
              <div className="mt-2">
                <button
                  onClick={() => setToolsExpanded(!toolsExpanded)}
                  className="flex items-center gap-1.5 text-xs text-gray-400 hover:text-gray-600 transition-colors"
                >
                  {toolsExpanded ? <ChevronDown className="w-3 h-3" /> : <ChevronRight className="w-3 h-3" />}
                  <Wrench className="w-3 h-3" />
                  <span>{message.tool_calls.length} tool{message.tool_calls.length > 1 ? 's' : ''} used</span>
                </button>

                {!toolsExpanded && (
                  <div className="flex flex-wrap gap-1 mt-1">
                    {message.tool_calls.map((tc, idx) => (
                      <span key={idx} className={cn(
                        'inline-flex items-center gap-1 px-1.5 py-0.5 text-[10px] font-mono',
                        'bg-gray-50 text-gray-500 rounded border border-gray-200',
                        tc.result && !tc.result.success ? 'border-red-200 text-red-500' : ''
                      )}>
                        {tc.result?.success !== false ? (
                          <CheckCircle2 className="w-2.5 h-2.5 text-emerald-500" />
                        ) : (
                          <XCircle className="w-2.5 h-2.5 text-red-400" />
                        )}
                        {tc.tool}
                      </span>
                    ))}
                  </div>
                )}

                {toolsExpanded && (
                  <div className="mt-2 space-y-1.5">
                    {message.tool_calls.map((tc, idx) => (
                      <div key={idx} className="p-2 bg-gray-50 rounded-lg border border-gray-200 text-xs">
                        <div className="flex items-center gap-1.5 mb-1">
                          <span className="font-medium font-mono text-gray-700">{tc.tool}</span>
                          {tc.result?.success !== false ? (
                            <CheckCircle2 className="w-3 h-3 text-emerald-500 ml-auto" />
                          ) : (
                            <XCircle className="w-3 h-3 text-red-500 ml-auto" />
                          )}
                        </div>
                        {tc.params && Object.keys(tc.params).length > 0 && (
                          <pre className="text-[10px] text-gray-400 overflow-x-auto font-mono mt-1">
                            {JSON.stringify(tc.params, null, 2)}
                          </pre>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Usage cost + mode badge */}
            {message.usage && !message.isStreaming && (message.usage.cost_usd ?? 0) > 0 && (
              <div className="flex items-center gap-2 mt-2 text-[10px] text-gray-400 flex-wrap">
                {message.usage.mode && (
                  <span className={cn(
                    'px-1.5 py-0.5 rounded font-medium',
                    message.usage.mode === 'advanced' ? 'bg-violet-100 text-violet-600' : 'bg-blue-50 text-blue-500'
                  )}>
                    {message.usage.mode === 'advanced' ? 'Advanced' : 'Standard'}
                  </span>
                )}
                <span>{(message.usage.input_tokens + message.usage.output_tokens).toLocaleString()} tokens</span>
                <span>·</span>
                <span className="font-medium text-gray-500">${(message.usage.cost_usd ?? 0) < 0.01 ? (message.usage.cost_usd ?? 0).toFixed(4) : (message.usage.cost_usd ?? 0).toFixed(2)}</span>
              </div>
            )}

            {/* Quick reply buttons */}
            {quickReplies && onQuickReply && (
              <div className="flex flex-wrap gap-2 mt-3">
                {quickReplies.map((reply, idx) => (
                  <button
                    key={idx}
                    onClick={() => onQuickReply(reply)}
                    className={cn(
                      'px-3 py-1.5 text-xs font-medium rounded-lg transition-all duration-150',
                      'border-2 border-[#1a1a2e] shadow-[2px_2px_0px_#1a1a2e]',
                      'hover:translate-x-[1px] hover:translate-y-[1px] hover:shadow-none',
                      idx === 0 ? 'bg-violet-500 text-white' : 'bg-white text-gray-700'
                    )}
                  >
                    {reply}
                  </button>
                ))}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};
